import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";
import { useInfiniteQuery, useQuery } from "react-query";
import { requireIdToken } from "../utils/firebaseAuth";
import axios from "axios";
import { NextSeo } from "next-seo";

import { toast } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import Link from "next/link";
import VoiceImage from "../components/VoiceImage";
import dayjs from "dayjs";
import {
  isFreshTraining,
  trainingProgress,
  trainingStartOf,
} from "@/lib/train/eta";
import RetryTraining from "../components/train/RetryTraining";
import relativeTime from "dayjs/plugin/relativeTime";
import millify from "millify";
import {
  Globe,
  Heart,
  Link2,
  Lock,
  MoreHorizontal,
  Music2,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Share2,
  Trash2,
  Upload,
} from "lucide-react";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import Dropdown from "../components/dropdown/Dropdown";
import { useAudioPlayer } from "../components/GlobalAudioPlayer";
import { useModal } from "../context/ModalContext";
import EditModelModal from "../components/modals/EditModelModal";
import DeleteModelModal from "../components/modals/DeleteModelModal";
import UploadVoiceModal from "../components/modals/UploadVoiceModal";
import { useRouter } from "next/router";
import {
  JamVoiceCard,
  JamCollectionCard,
  JamCardSkeleton,
} from "../components/home/JamCards";

dayjs.extend(relativeTime);

// Status chips filter what is already loaded; the API has no status param.
// Each one maps to a field getUserConversions actually returns.
// Locked covers (saved by a free user, not run) aren't being made.
const isLocked = (c) => c?.status === "locked" || c?.status === "unlocking";
const isMaking = (c) =>
  c?.status !== "success" && c?.status !== "failed" && !isLocked(c);
const COVER_FILTERS = [
  { id: "all", label: "All", test: () => true },
  { id: "ready", label: "Ready", test: (c) => c?.status === "success" },
  { id: "making", label: "Making", test: isMaking },
  { id: "failed", label: "Didn't work", test: (c) => c?.status === "failed" },
  { id: "locked", label: "Locked", test: isLocked },
  { id: "duets", label: "Duets", test: (c) => c?.models?.length > 1 },
  { id: "video", label: "With video", test: (c) => !!c?.outputUrls?.video },
];

const formatSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secondsLeft = Math.floor(seconds % 60);
  return `${minutes}:${secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}`;
};

const formatGroupDate = (date) => {
  const today = dayjs().startOf("day");
  const sectionDate = dayjs(date).startOf("day");
  if (sectionDate.isSame(today)) return "Today";
  if (sectionDate.isSame(today.subtract(1, "day"))) return "Yesterday";
  return sectionDate.format(
    sectionDate.year() === today.year() ? "D MMMM" : "D MMMM YYYY",
  );
};

const coverVoices = (c) =>
  c?.models?.length ? c.models : c?.model ? [c.model] : [];

const voiceNames = (voices) => {
  const names = voices.map((m) => m?.artistname).filter(Boolean);
  if (names.length <= 1) return names[0] || "";
  return names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
};

const Pill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold ${
      active
        ? "tw-border-jam-ink tw-bg-jam-ink tw-text-white"
        : "tw-border-jam-line tw-bg-white tw-text-jam-ink hover:tw-border-jam-purple"
    }`}
  >
    {children}
  </button>
);

const SectionLabel = ({ children }) => (
  <div className="tw-px-1 tw-py-1.5 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.06em] tw-text-jam-muted md:tw-text-[13px]">
    {children}
  </div>
);

const Loading = () => (
  <div className="tw-flex tw-justify-center tw-py-3">
    <Spinner size={5} className="tw-border-3" />
  </div>
);

const Empty = ({ title, children }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-[32px] tw-bg-jam-lav tw-px-6 tw-py-10 tw-text-center">
    <div className="jam-display tw-text-2xl tw-font-extrabold tw-text-jam-ink">
      {title}
    </div>
    {children}
  </div>
);

const CoverRow = ({ conversion, isToday }) => {
  const { playAudio, info, pauseAudio, isPlaying } = useAudioPlayer();
  const voices = coverVoices(conversion);
  const names = voiceNames(voices);
  const status = conversion?.status;
  const ready = status === "success";
  const failed = status === "failed";
  const making = !ready && !failed;
  const hasVideo = !!conversion?.outputUrls?.video;
  const audio = conversion?.outputUrls?.audio || conversion?.convertedUrl;
  const title = conversion?.name || `#${conversion?.shortid}`;
  const href = `/conversion/${conversion?.shortid}`;
  const progress = Math.round(Number(conversion?.progress) || 0);
  const thisPlaying = isPlaying && info?.id === conversion?._id;

  const togglePlay = () => {
    if (!ready || !audio) return;
    if (thisPlaying) return pauseAudio();
    playAudio({
      audio,
      info: {
        ...conversion,
        image: voices[0]?.image?.url,
        name: conversion?.name ? conversion.name : conversion?.shortid,
        id: conversion?._id,
        artistNames: names,
      },
    });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(
      `https://www.jammable.com/conversion/${conversion?.shortid}`,
    );
    toast.success("Link copied");
  };

  const chip = ready
    ? hasVideo
      ? { label: "Ready · with video", cls: "tw-bg-jam-lime tw-text-jam-ink" }
      : { label: "Ready", cls: "tw-bg-jam-lav tw-text-jam-ink" }
    : failed
      ? { label: "Didn't work", cls: "tw-bg-[#FDE4EC] tw-text-[#8A1C3B]" }
      : isLocked(conversion)
        ? {
            label: "Locked · unlock to make",
            cls: "tw-bg-jam-ink tw-text-jam-lime",
          }
        : {
            label: progress > 0 ? `Making · ${progress}%` : "Making",
            cls: "tw-bg-jam-purple tw-text-white",
          };

  const meta = [
    names,
    conversion?.duration ? formatSeconds(Number(conversion.duration)) : null,
    isToday
      ? dayjs(conversion?.createdAt).fromNow()
      : dayjs(conversion?.createdAt).format("h:mm A"),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-3 tw-rounded-[22px] tw-py-2.5 tw-pl-2.5 tw-pr-3 md:tw-gap-3.5 md:tw-rounded-3xl md:tw-pr-3.5 ${
        making ? "tw-bg-jam-lav" : "tw-bg-white"
      }`}
    >
      <button
        type="button"
        aria-label={thisPlaying ? "Pause" : "Play"}
        disabled={!ready || !audio}
        onClick={togglePlay}
        className={`tw-order-last tw-flex tw-h-[38px] tw-w-[38px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 md:tw-order-none md:tw-h-[46px] md:tw-w-[46px] ${
          ready && audio
            ? "tw-cursor-pointer tw-bg-jam-purple tw-text-white"
            : "tw-bg-jam-line tw-text-jam-muted"
        } ${!ready ? "tw-hidden md:tw-flex" : ""}`}
      >
        {thisPlaying ? (
          <Pause size={14} fill="currentColor" />
        ) : (
          <Play size={14} fill="currentColor" className="tw-ml-0.5" />
        )}
      </button>

      <Link href={href} className="tw-flex tw-shrink-0" aria-label={title}>
        {voices.slice(0, 2).map((model, i) => (
          <span
            key={model?._id || i}
            style={{ marginLeft: i ? -18 : 0, zIndex: 2 - i }}
            className="tw-relative tw-h-12 tw-w-12 tw-overflow-hidden tw-rounded-2xl tw-border-[3px] tw-border-solid tw-border-white tw-bg-jam-lav md:tw-h-11 md:tw-w-11 md:tw-rounded-[14px]"
          >
            <VoiceImage
              model={model}
              fill
              sizes="48px"
              className="tw-object-cover"
            />
          </span>
        ))}
      </Link>

      <Link
        href={href}
        className="tw-min-w-0 tw-flex-grow tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
      >
        <div className="tw-truncate tw-text-sm tw-font-extrabold md:tw-text-[15px]">
          {title}
        </div>
        <div className="tw-truncate tw-text-xs tw-font-semibold tw-text-jam-muted md:tw-text-[13px]">
          {meta}
        </div>
        {/* Phone has no chip column, so non-ready state goes under the title. */}
        {!ready && (
          <div
            className={`tw-truncate tw-text-xs tw-font-bold md:tw-hidden ${
              failed ? "tw-text-[#8A1C3B]" : "tw-text-jam-purple"
            }`}
          >
            {chip.label}
          </div>
        )}
      </Link>

      <span
        className={`tw-hidden tw-whitespace-nowrap tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold md:tw-inline-flex ${chip.cls}`}
      >
        {chip.label}
      </span>

      <div className="tw-hidden tw-w-24 tw-justify-end md:tw-flex">
        {ready && (
          <button
            type="button"
            onClick={copyLink}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
          >
            <Link2 size={14} strokeWidth={2.6} />
            Share
          </button>
        )}
        {failed && (
          <Link
            href={href}
            className="tw-text-[13px] tw-font-extrabold tw-text-jam-purple tw-no-underline"
          >
            Try again
          </Link>
        )}
      </div>

      {failed && (
        <Link
          href={href}
          className="tw-shrink-0 tw-rounded-full tw-bg-jam-lav tw-px-2.5 tw-py-1.5 tw-text-[11px] tw-font-extrabold tw-text-jam-purple tw-no-underline md:tw-hidden"
        >
          Try again
        </Link>
      )}
    </div>
  );
};

// Edit / delete / upload modals sit in the F frame (no border or shadow).
// The Library is the only place to manage your voices: /myvoices redirects
// here.
const MODAL_CONFIG = {
  className:
    "md:tw-min-w-[450px] md:tw-max-w-lg !tw-border-0 !tw-shadow-none !tw-rounded-t-[32px] md:!tw-rounded-[32px]",
};
const UPLOAD_MODAL_CONFIG = {
  className:
    "md:tw-min-w-[520px] md:tw-max-w-xl !tw-border-0 !tw-shadow-none !tw-rounded-t-[32px] md:!tw-rounded-[32px] tw-max-h-[94vh] tw-overflow-y-auto",
};

const MENU_CLASS =
  "!tw-border-2 !tw-border-jam-line !tw-shadow-none tw-bg-white tw-p-1.5 tw-min-w-[180px]";
const MENU_ITEM_CLASS =
  "tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-2xl tw-p-2.5 tw-pl-3 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-outline-none hover:tw-bg-jam-lav focus:tw-bg-jam-lav";

const VoiceMenu = ({ onEdit, onShare, onDelete }) => (
  <Dropdown
    className={MENU_CLASS}
    dropdownItems={
      <>
        {onEdit && (
          <DropdownMenuItem onClick={onEdit} className={MENU_ITEM_CLASS}>
            <Pencil size={15} strokeWidth={2.5} />
            Edit voice
          </DropdownMenuItem>
        )}
        {onShare && (
          <DropdownMenuItem onClick={onShare} className={MENU_ITEM_CLASS}>
            <Share2 size={15} strokeWidth={2.5} />
            Copy link
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onDelete}
          className={`${MENU_ITEM_CLASS} !tw-text-[#8A1C3B] hover:!tw-bg-[#FDE4EC] focus:!tw-bg-[#FDE4EC]`}
        >
          <Trash2 size={15} strokeWidth={2.5} />
          Delete voice
        </DropdownMenuItem>
      </>
    }
  >
    <button
      type="button"
      aria-label="Voice options"
      className="tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line"
    >
      <MoreHorizontal size={18} strokeWidth={2.5} />
    </button>
  </Dropdown>
);

const TrainedVoiceCard = ({ model, onEdited }) => {
  const { openModal, closeModal } = useModal();
  // Keyed off `failed`, not failCount: the training webhook sets failed=true
  // and clears `training`, and failCount survives a later successful retrain.
  const isFailed = model?.failed === true;
  const isTraining = model?.training === true && !isFailed;
  const isPublic = !model?.private && model?.isPublic;
  // While training: how far along a voice with this much audio usually is
  // by now (lib/train/eta). Re-read every minute so the bar and the minutes
  // left move between the library's refreshes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isTraining) return;
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [isTraining]);
  const progress = isTraining
    ? trainingProgress(model?.trainingSeconds, trainingStartOf(model), now)
    : null;

  const openEdit = () =>
    openModal(
      "edit-model-modal",
      <EditModelModal model={model} after={onEdited} />,
      UPLOAD_MODAL_CONFIG,
    );
  const openDelete = () =>
    openModal(
      "delete-model-modal",
      <DeleteModelModal
        model={model}
        closeModal={() => closeModal("delete-model-modal")}
        after={onEdited}
      />,
      MODAL_CONFIG,
    );
  const share = () => {
    navigator.clipboard.writeText(
      `https://www.jammable.com/${model?.urlParam}`,
    );
    toast.success("Link copied");
  };

  const badge = isTraining ? (
    <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-purple tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-white">
      <span className="tw-h-1.5 tw-w-1.5 tw-animate-pulse tw-rounded-full tw-bg-jam-lime" />
      Training
    </span>
  ) : isFailed ? (
    <span className="tw-rounded-full tw-bg-[#FDE4EC] tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-[#8A1C3B]">
      Didn&apos;t work
    </span>
  ) : (
    <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-jam-ink">
      {isPublic ? (
        <Globe size={13} strokeWidth={2.75} />
      ) : (
        <Lock size={13} strokeWidth={2.75} />
      )}
      {isPublic ? "Public" : "Private"}
    </span>
  );

  return (
    <div
      className={`tw-flex tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-[32px] tw-border-2 tw-border-solid tw-bg-white tw-text-jam-ink tw-transition-colors ${
        isTraining
          ? "tw-border-jam-purple"
          : "tw-border-jam-line hover:tw-border-jam-soft"
      }`}
    >
      <div className="tw-relative tw-flex tw-h-[176px] tw-items-center tw-justify-center tw-bg-jam-lav">
        <div className="tw-absolute tw-left-3.5 tw-top-3.5">{badge}</div>
        {!isTraining && (
          <div className="tw-absolute tw-right-3.5 tw-top-3.5">
            <VoiceMenu
              onEdit={isFailed ? null : openEdit}
              onShare={isFailed ? null : share}
              onDelete={openDelete}
            />
          </div>
        )}
        <span className="tw-relative tw-flex tw-h-[120px] tw-w-[120px] tw-items-center tw-justify-center">
          {isTraining && (
            <span className="tw-absolute tw-inset-0 tw-animate-spin tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-border-t-jam-purple [animation-duration:1.6s]" />
          )}
          <span
            className={`tw-relative tw-overflow-hidden tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-bg-jam-line ${
              isTraining
                ? "tw-h-[96px] tw-w-[96px]"
                : "tw-h-[120px] tw-w-[120px]"
            } ${isFailed ? "tw-opacity-60 tw-grayscale" : ""}`}
          >
            <VoiceImage
              model={model}
              fill
              sizes="120px"
              className="tw-object-cover"
            />
          </span>
        </span>
      </div>

      <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-3 tw-p-5">
        <div className="tw-min-w-0">
          <div className="jam-display tw-truncate tw-text-[20px] tw-font-extrabold tw-leading-tight">
            {model?.artistname}
          </div>
          <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-[13px] tw-font-bold tw-text-jam-muted">
            {isTraining ? (
              <span>
                {progress
                  ? "We'll email you when it's ready."
                  : "We'll let you know when it's ready."}
              </span>
            ) : isFailed ? (
              <span>{model?.retry?.reason || "Training didn't finish."}</span>
            ) : (
              <>
                <span>Trained {dayjs(model?.createdAt).format("D MMM")}</span>
                <span className="tw-flex tw-items-center tw-gap-1">
                  <Music2 size={13} strokeWidth={2.5} />
                  {millify(model?.uses || 0)}
                </span>
                <span className="tw-flex tw-items-center tw-gap-1">
                  <Heart size={13} strokeWidth={2.5} />
                  {millify(model?.likes || 0)}
                </span>
              </>
            )}
          </div>
        </div>
        {isTraining ? (
          progress ? (
            <div
              role="progressbar"
              aria-label="Training progress, estimated"
              aria-valuenow={progress.pct}
              aria-valuemin={0}
              aria-valuemax={100}
              className="tw-relative tw-mt-auto tw-flex tw-h-12 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-jam-lav"
            >
              <div
                className="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-jam-soft tw-transition-all tw-duration-1000"
                style={{ width: `${progress.pct}%` }}
              />
              <span className="tw-relative tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
                {progress.late
                  ? "Taking longer than usual"
                  : `Usually ready in ${progress.leftLabel}`}
              </span>
            </div>
          ) : (
            <div className="tw-mt-auto tw-h-12 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
          )
        ) : isFailed ? (
          <RetryTraining model={model} onRetried={onEdited} />
        ) : (
          <Link
            href={`/create?voice=${encodeURIComponent(model?.urlParam || model?._id)}`}
            className="tw-mt-auto tw-flex tw-h-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline tw-transition-colors hover:tw-bg-jam-deep"
          >
            Make a cover
          </Link>
        )}
      </div>
    </div>
  );
};

// Brand-new account: nothing made yet. No free covers exist (removed
// 2026-06-02), so the copy points at the plan price, not a free allowance.
const NewAccountEmpty = () => {
  const { data } = useQuery("trending-voices", () =>
    axios.get("/api/home/getTrendingVoices"),
  );
  const picks = (data?.data?.models || []).slice(0, 3);

  return (
    <section className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-rounded-[32px] tw-bg-jam-lav tw-px-5 tw-py-10 tw-text-center md:tw-gap-[18px] md:tw-rounded-[44px] md:tw-p-14">
      {picks.length > 0 && (
        <div className="tw-flex">
          {picks.map((model, i) => (
            <span
              key={model._id}
              style={{ marginLeft: i ? -20 : 0, zIndex: 3 - i }}
              className="tw-relative tw-h-16 tw-w-16 tw-overflow-hidden tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-bg-jam-line md:tw-h-[76px] md:tw-w-[76px]"
            >
              <VoiceImage
                model={model}
                fill
                sizes="76px"
                className="tw-object-cover"
              />
            </span>
          ))}
        </div>
      )}
      <h2 className="jam-display tw-m-0 tw-text-[28px] tw-font-extrabold tw-tracking-[-0.04em] tw-text-jam-ink md:tw-text-[40px]">
        Your covers will live here
      </h2>
      <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted md:tw-text-base">
        Make your first one in about a minute. Plans start at $1.99 for your
        first month.
      </p>
      {picks.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2.5">
          {picks.map((model) => (
            <Link
              key={model._id}
              href={`/${model.urlParam}`}
              className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-white tw-py-2 tw-pl-2 tw-pr-[18px] tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
            >
              <span className="tw-relative tw-h-9 tw-w-9 tw-overflow-hidden tw-rounded-full tw-bg-jam-line">
                <VoiceImage
                  model={model}
                  fill
                  sizes="36px"
                  className="tw-object-cover"
                />
              </span>
              Make a {model.artistname} cover
            </Link>
          ))}
        </div>
      )}
      <Link
        href="/create"
        className="tw-mt-1.5 tw-rounded-full tw-bg-jam-purple tw-px-[30px] tw-py-4 tw-text-base tw-font-extrabold tw-text-white tw-no-underline"
      >
        {picks.length > 0 ? "Or start from scratch" : "Make a cover"}
      </Link>
    </section>
  );
};

const TABS = ["covers", "voices", "collections"];
const VOICE_TYPES = ["uploaded", "saved"];

const LibraryPage = () => {
  const { user } = useAuth();
  const router = useRouter();
  const { openModal, closeModal } = useModal();
  // ?tab=voices&type=saved opens straight on a tab (old /myvoices links
  // redirect here with them).
  const [activeTab, setActiveTabState] = useState("covers");
  const [voiceType, setVoiceTypeState] = useState("uploaded");
  useEffect(() => {
    if (!router.isReady) return;
    const { tab, type } = router.query;
    if (TABS.includes(tab)) setActiveTabState(tab);
    if (VOICE_TYPES.includes(type)) setVoiceTypeState(type);
  }, [router.isReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const syncQuery = (next) =>
    router.replace(
      { pathname: "/library", query: { ...router.query, ...next } },
      undefined,
      { shallow: true, scroll: false },
    );
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    syncQuery({ tab });
  };
  const setVoiceType = (type) => {
    setVoiceTypeState(type);
    syncQuery({ type });
  };
  const openUploadVoice = () =>
    openModal(
      "upload-voice-modal",
      <UploadVoiceModal closeModal={() => closeModal("upload-voice-modal")} />,
      UPLOAD_MODAL_CONFIG,
    );
  const [search, setSearch] = useState("");
  const [fetching, setFetching] = useState(false);
  const [coverFilter, setCoverFilter] = useState("all");

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Fetch user voices
  const getMyVoices = async ({ pageParam = 0 }) => {
    const token = await requireIdToken();

    const res = await axios.get(`/api/user/getMyVoices?page=${pageParam}`, {
      headers: { Authorization: token },
      params: {
        limit: 30,
        type: voiceType,
        search: search,
      },
    });

    return res.data;
  };

  // Fetch user covers/conversions - using the same API as history.js
  const getMyCovers = async ({ pageParam = 0 }) => {
    const token = await requireIdToken();

    const res = await axios.get(
      `/api/user/getUserConversions?page=${pageParam}`,
      {
        headers: { Authorization: token },
        params: {
          limit: 20,
          search: search,
        },
      },
    );

    return res.data;
  };

  // Fetch user collections
  const getMyCollections = async ({ pageParam = 0 }) => {
    const token = await requireIdToken();

    const res = await axios.get(`/api/collection/getUserCollections`, {
      headers: { Authorization: token },
      params: {
        page: pageParam,
        limit: 30,
        search: search,
        userId: user?.firebaseUid,
      },
    });

    return res.data;
  };

  // Query for voices
  const voices = useInfiniteQuery(
    ["my-voices", search, voiceType],
    getMyVoices,
    {
      getNextPageParam: (lastpage, allpages) => {
        const maxPages = Math.ceil(lastpage?.length / 30);
        const nextPage = allpages.length + 1;
        return nextPage <= maxPages ? nextPage : null;
      },
      enabled: !!user && activeTab === "voices",
      // A voice that finishes while the page is open flips to ready without
      // a reload. Polls only while one started in the last few hours is
      // still training (lib/train/eta), once a minute, and not in a
      // background tab (react-query's default).
      refetchInterval: (data) =>
        data?.pages?.some((p) => (p?.models ?? []).some(isFreshTraining))
          ? 60000
          : false,
    },
  );

  // Query for covers - using the same structure as history.js
  const covers = useInfiniteQuery(["my-covers", search], getMyCovers, {
    getNextPageParam: (lastpage, allpages) => {
      const maxPages = Math.ceil(lastpage?.length / 20);
      const nextPage = allpages.length + 1;
      return nextPage <= maxPages ? nextPage : null;
    },
    enabled: !!user && activeTab === "covers",
  });

  // Query for collections
  const collections = useInfiniteQuery(
    ["my-collections", search],
    getMyCollections,
    {
      getNextPageParam: (lastpage, allpages) => {
        const maxPages = Math.ceil(lastpage?.total / 30);
        const nextPage = allpages.length + 1;
        return nextPage <= maxPages ? nextPage : null;
      },
      enabled: !!user && activeTab === "collections",
    },
  );

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = async (e) => {
      if (
        !fetching &&
        window.innerHeight + e.target.documentElement.scrollTop + 800 >=
          e.target.documentElement.scrollHeight
      ) {
        setFetching(true);

        if (
          activeTab === "voices" &&
          voices.hasNextPage &&
          !voices.isFetchingNextPage
        ) {
          await voices.fetchNextPage();
        } else if (
          activeTab === "covers" &&
          covers.hasNextPage &&
          !covers.isFetchingNextPage
        ) {
          await covers.fetchNextPage();
        } else if (
          activeTab === "collections" &&
          collections.hasNextPage &&
          !collections.isFetchingNextPage
        ) {
          await collections.fetchNextPage();
        }

        setFetching(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, voices, covers, collections, fetching]);

  // Each page is already grouped by day server-side; a day can straddle two
  // pages, so merge groups with the same date before rendering.
  const coverGroups = (() => {
    const filter =
      COVER_FILTERS.find((f) => f.id === coverFilter) || COVER_FILTERS[0];
    const byDate = new Map();
    covers.data?.pages.forEach((page) =>
      page?.conversions?.forEach((section) => {
        const items = (section?.conversions || []).filter(filter.test);
        if (!byDate.has(section._id)) {
          byDate.set(section._id, { ...section, conversions: [] });
        }
        byDate.get(section._id).conversions.push(...items);
      }),
    );
    return [...byDate.values()].filter((g) => g.conversions.length > 0);
  })();

  const allCollections =
    collections.data?.pages.flatMap((page) => page?.collections || []) || [];
  const allVoices =
    voices.data?.pages.flatMap((page) => page?.models || []) || [];

  const isNewAccount =
    activeTab === "covers" &&
    !search &&
    covers.data?.pages[0]?.conversions?.length === 0;

  // Counts only for what has loaded; tabs fetch lazily.
  const coverCount = covers.data?.pages[0]?.length;
  const collectionCount = collections.data ? allCollections.length : undefined;
  const voiceCount = voices.data?.pages[0]?.length;

  const tabsData = [
    { id: "covers", label: "Covers", count: coverCount },
    { id: "voices", label: "Voices" },
    { id: "collections", label: "Collections", count: collectionCount },
  ];

  const renderCovers = () => {
    if (covers.isLoading) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {[0, 1, 2, 3].map((i) => (
            <JamCardSkeleton key={i} className="tw-h-16 tw-rounded-3xl" />
          ))}
        </div>
      );
    }

    if (isNewAccount) return <NewAccountEmpty />;

    if (coverGroups.length === 0 && !covers.hasNextPage) {
      return (
        <Empty title={search ? "No covers match" : "Nothing here yet"}>
          <div className="tw-text-sm tw-font-semibold tw-text-jam-muted">
            Try another filter or search.
          </div>
        </Empty>
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-5">
        {coverGroups.map((group) => {
          const label = formatGroupDate(group.date || group._id);
          return (
            <section key={group._id} className="tw-flex tw-flex-col tw-gap-2">
              <SectionLabel>{label}</SectionLabel>
              {group.conversions.map((conversion) => (
                <CoverRow
                  key={conversion?._id}
                  conversion={conversion}
                  isToday={label === "Today"}
                />
              ))}
            </section>
          );
        })}
        {covers.isFetchingNextPage ? (
          <Loading />
        ) : (
          // Scroll loading never fires when a filter leaves the page short,
          // so the next page is also one tap away.
          covers.hasNextPage && (
            <button
              type="button"
              onClick={() => covers.fetchNextPage()}
              className="tw-cursor-pointer tw-self-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[22px] tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink"
            >
              Show older covers
            </button>
          )
        )}
      </div>
    );
  };

  const renderVoices = () => {
    if (voices.isLoading) {
      return (
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
          {[0, 1, 2].map((i) => (
            <JamCardSkeleton key={i} className="tw-h-48 tw-rounded-[32px]" />
          ))}
        </div>
      );
    }

    if (voiceType === "saved") {
      if (allVoices.length === 0) {
        return (
          <Empty title="No saved voices yet">
            <div className="tw-text-sm tw-font-semibold tw-text-jam-muted">
              Voices you like show up here.
            </div>
            <Link
              href="/models"
              className="tw-mt-2 tw-rounded-full tw-bg-jam-purple tw-px-6 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline"
            >
              Browse voices
            </Link>
          </Empty>
        );
      }
      return (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <SectionLabel>Saved voices</SectionLabel>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3.5 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-6">
            {allVoices.map((model) => (
              <JamVoiceCard key={model._id} model={model} />
            ))}
          </div>
          {voices.isFetchingNextPage && <Loading />}
        </div>
      );
    }

    const passes = user?.customModels;
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3">
          {allVoices.map((model) => (
            <TrainedVoiceCard
              key={model._id}
              model={model}
              onEdited={() => voices.refetch()}
            />
          ))}
          <Link
            href="/train"
            className="tw-flex tw-min-h-[180px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-[32px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-5 tw-text-center tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-border-jam-purple"
          >
            <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple">
              <Plus size={22} strokeWidth={2.6} />
            </span>
            <div className="tw-text-base tw-font-extrabold">
              {allVoices.length ? "Train another voice" : "Train your voice"}
            </div>
            {typeof passes === "number" && (
              <div className="tw-text-[13px] tw-font-semibold tw-text-jam-muted">
                {passes} training {passes === 1 ? "pass" : "passes"} left
              </div>
            )}
          </Link>
          <button
            type="button"
            onClick={openUploadVoice}
            className="tw-flex tw-min-h-[180px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-[32px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-5 tw-text-center tw-text-jam-ink tw-transition-colors hover:tw-border-jam-purple"
          >
            <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple">
              <Upload size={22} strokeWidth={2.6} />
            </span>
            <div className="tw-text-base tw-font-extrabold">
              Upload a voice you have
            </div>
            <div className="tw-text-[13px] tw-font-semibold tw-text-jam-muted">
              Already have an RVC model? Add it here.
            </div>
          </button>
        </div>
        {voices.isFetchingNextPage && <Loading />}
      </div>
    );
  };

  const renderCollections = () => {
    if (collections.isLoading) {
      return (
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <JamCardSkeleton key={i} className="tw-h-[170px]" />
          ))}
        </div>
      );
    }

    if (allCollections.length === 0) {
      return (
        <Empty title="No collections yet">
          <div className="tw-text-sm tw-font-semibold tw-text-jam-muted">
            Group voices you like into a collection from any voice page.
          </div>
        </Empty>
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-3 lg:tw-grid-cols-4">
          {allCollections.map((collection) => (
            <JamCollectionCard
              key={collection._id}
              collection={{
                ...collection,
                // getUserCollections returns the raw docs, without the
                // modelsCount the home endpoints add.
                modelsCount:
                  collection.modelsCount ?? collection.models?.length ?? 0,
              }}
            />
          ))}
        </div>
        {collections.isFetchingNextPage && <Loading />}
      </div>
    );
  };

  const renderTabContent = () => {
    if (!user) {
      return (
        <Empty title="Sign in to see your library">
          <Link
            href="/login"
            className="tw-mt-2 tw-rounded-full tw-bg-jam-purple tw-px-6 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline"
          >
            Sign in
          </Link>
        </Empty>
      );
    }
    if (activeTab === "voices") return renderVoices();
    if (activeTab === "collections") return renderCollections();
    return renderCovers();
  };

  const showSearch = !!user && activeTab !== "collections" && !isNewAccount;

  return (
    <>
      <NextSeo
        title="My Library | Jammable"
        description="Access all your voices, covers, and collections in one place."
      />
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-4 tw-px-4 tw-pb-24 tw-pt-2 tw-text-jam-ink md:tw-gap-[22px] md:tw-px-8 md:tw-pt-4">
        <div className="tw-flex tw-items-end tw-justify-between tw-gap-4">
          <div>
            <h1 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-tracking-[-0.045em] md:tw-text-[52px]">
              Library
            </h1>
            <div className="tw-mt-1 tw-hidden tw-text-[15px] tw-font-bold tw-text-jam-muted sm:tw-block">
              Everything you&apos;ve made, trained and saved
            </div>
          </div>
          {activeTab === "voices" ? (
            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
              {user && (
                <button
                  type="button"
                  onClick={openUploadVoice}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-4 tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line md:tw-px-5 md:tw-py-[15px] md:tw-text-[15px]"
                >
                  <Upload size={16} strokeWidth={2.75} />
                  <span className="tw-hidden sm:tw-inline">Upload a voice</span>
                </button>
              )}
              <Link
                href="/train"
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-ink tw-px-4 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline md:tw-px-6 md:tw-py-[15px] md:tw-text-[15px]"
              >
                <Plus size={16} strokeWidth={3} />
                Train a new voice
              </Link>
            </div>
          ) : (
            <Link
              href="/create"
              className="tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-purple tw-px-4 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline md:tw-px-6 md:tw-py-[15px] md:tw-text-[15px]"
            >
              <Plus size={16} strokeWidth={3} />
              New cover
            </Link>
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between md:tw-gap-4">
          <div className="tw-flex tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-[13px] tw-font-extrabold md:tw-self-start md:tw-p-[5px] md:tw-text-[15px]">
            {tabsData.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`tw-flex-grow tw-cursor-pointer tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-px-3 tw-py-2.5 md:tw-flex-grow-0 md:tw-px-5 ${
                  activeTab === tab.id
                    ? "tw-bg-white tw-text-jam-purple"
                    : "tw-bg-transparent tw-text-jam-ink"
                }`}
              >
                {tab.label}
                {typeof tab.count === "number" && tab.count > 0 && (
                  <span className="tw-ml-1 tw-text-jam-muted">
                    {millify(tab.count)}
                  </span>
                )}
              </button>
            ))}
          </div>

          {showSearch && (
            <label className="tw-flex tw-max-w-[420px] tw-flex-grow tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-jam-lav tw-px-[18px] tw-py-[13px]">
              <Search
                size={16}
                strokeWidth={2.5}
                className="tw-shrink-0 tw-text-jam-muted"
              />
              <input
                type="text"
                aria-label={`Search your ${activeTab}`}
                placeholder={
                  activeTab === "voices"
                    ? "Search by voice"
                    : "Search by cover name"
                }
                value={search}
                onChange={handleSearchChange}
                className="tw-w-full tw-border-0 tw-bg-transparent tw-text-sm tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted"
              />
            </label>
          )}
        </div>

        {user && activeTab === "covers" && !isNewAccount && (
          <div className="tw-flex tw-gap-1.5 tw-overflow-x-auto md:tw-gap-2">
            {COVER_FILTERS.map((f) => (
              <Pill
                key={f.id}
                active={coverFilter === f.id}
                onClick={() => setCoverFilter(f.id)}
              >
                {f.label}
              </Pill>
            ))}
          </div>
        )}

        {user && activeTab === "voices" && (
          <div className="tw-flex tw-gap-1.5">
            <Pill
              active={voiceType === "uploaded"}
              onClick={() => setVoiceType("uploaded")}
            >
              Trained by me
              {voiceType === "uploaded" && voiceCount ? ` · ${voiceCount}` : ""}
            </Pill>
            <Pill
              active={voiceType === "saved"}
              onClick={() => setVoiceType("saved")}
            >
              Saved
              {voiceType === "saved" && voiceCount ? ` · ${voiceCount}` : ""}
            </Pill>
          </div>
        )}

        <div className="tw-w-full">{renderTabContent()}</div>
      </div>
    </>
  );
};

export default LibraryPage;
